// Combobox.tsx — themed autocomplete input with a styleable suggestion
// popover. Uses the same dashed-border + bootstrap-var palette as <Text> /
// <Dropdown>, but unlike <input list=...> the popover is a real DOM list so
// it can be CSS-styled (option-panel rules in the app's index.css apply).
//
// Behavior:
//   - typing filters by case-insensitive substring of value or label
//   - ArrowDown / ArrowUp move highlight, Enter selects, Escape closes
//   - blur closes after a short delay so a click on a suggestion still fires
import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface ComboboxOption {
    value: string;
    label?: string;
}

export interface ComboboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
    value: string;
    onChange: (next: string) => void;
    options: (string | ComboboxOption)[];
    minWidth?: number;
    textVariant?: string;
    borderVariant?: string;
    /**
     * Filter `options` by substring of the typed value. Default true.
     *
     * Set false when the options are already the answer to what was
     * typed — a server-side search, say. Substring filtering a remote
     * result set silently empties the popover whenever the backend
     * matched something the literal string doesn't appear in: type
     * "llama 3" and `meta-llama/Meta-Llama-3-8B` is dropped by the very
     * filter meant to help.
     */
    filterOptions?: boolean;
}

const getBootstrapColor = (variant?: string) =>
    variant ? `var(--bs-${variant})` : undefined;

const norm = (opt: string | ComboboxOption): ComboboxOption =>
    typeof opt === 'string' ? { value: opt } : opt;

export const Combobox: React.FC<ComboboxProps> = ({
    value,
    onChange,
    options,
    placeholder,
    minWidth = 1,
    textVariant,
    borderVariant,
    filterOptions = true,
    style,
    className,
    onFocus,
    onBlur,
    onKeyDown,
    ...rest
}) => {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState<number>(-1);
    const wrapperRef = useRef<HTMLSpanElement | null>(null);

    const items = useMemo(() => options.map(norm), [options]);

    const filtered = useMemo(() => {
        if (!filterOptions) return items;
        const q = value.trim().toLowerCase();
        if (!q) return items;
        return items.filter((o) => {
            const v = o.value.toLowerCase();
            const l = (o.label ?? o.value).toLowerCase();
            return v.includes(q) || l.includes(q);
        });
    }, [items, value, filterOptions]);

    // Reset highlight when filtered list changes.
    useEffect(() => { setHighlight(filtered.length > 0 ? 0 : -1); }, [filtered.length]);

    // Click-outside closes.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (!wrapperRef.current) return;
            if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const measureLength = (value || placeholder || '').length;
    const effectiveWidth = Math.max(measureLength, minWidth);
    const color = getBootstrapColor(textVariant);
    const borderColor = getBootstrapColor(borderVariant);

    const commit = (v: string) => {
        onChange(v);
        setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.min(h + 1, filtered.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => Math.max(h - 1, 0));
        } else if (e.key === 'Enter') {
            if (open && highlight >= 0 && highlight < filtered.length) {
                e.preventDefault();
                commit(filtered[highlight].value);
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <span ref={wrapperRef} className="of-themed-combobox" style={{ position: 'relative', display: 'inline-block', width: 'auto', ...((style as React.CSSProperties)?.width ? { width: (style as React.CSSProperties).width } : {}) }}>
            <input
                type="text"
                value={value}
                placeholder={placeholder}
                onChange={(e) => { onChange(e.target.value); setOpen(true); }}
                onFocus={(e) => { setOpen(true); onFocus?.(e); }}
                onBlur={(e) => { window.setTimeout(() => setOpen(false), 120); onBlur?.(e); }}
                onKeyDown={handleKeyDown}
                style={{
                    background: 'transparent',
                    border: `1px dashed ${borderColor || 'var(--bs-border-color, #666)'}`,
                    color: color || 'var(--bs-body-color, inherit)',
                    fontFamily: 'inherit',
                    padding: '0.5rem 1rem',
                    display: 'inline-block',
                    width: `calc(${effectiveWidth}ch + 3rem)`,
                    transition: 'width 0.3s ease-out',
                    ...style,
                }}
                className={['of-themed-input', className].filter(Boolean).join(' ')}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={open}
                {...rest}
            />
            {open && filtered.length > 0 && (
                <ul
                    className="of-themed-combobox-list"
                    role="listbox"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        margin: '2px 0 0 0',
                        padding: '4px 0',
                        listStyle: 'none',
                        background: 'var(--bs-body-bg)',
                        color: 'var(--bs-body-color)',
                        border: '1px solid var(--bs-border-color, #666)',
                        borderRadius: 4,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        maxHeight: '14em',
                        overflowY: 'auto',
                        zIndex: 1000,
                        fontFamily: 'inherit',
                        fontSize: '0.9em',
                    }}
                >
                    {filtered.map((opt, i) => (
                        <li
                            key={opt.value}
                            role="option"
                            aria-selected={highlight === i}
                            onMouseDown={(e) => { e.preventDefault(); commit(opt.value); }}
                            onMouseEnter={() => setHighlight(i)}
                            style={{
                                padding: '4px 12px',
                                cursor: 'pointer',
                                background: highlight === i
                                    ? 'rgba(var(--bs-primary-rgb), 0.18)'
                                    : 'transparent',
                            }}
                        >
                            {opt.label ?? opt.value}
                        </li>
                    ))}
                </ul>
            )}
        </span>
    );
};

export default Combobox;
